<script lang="ts" setup>
import { useMutation, useQuery } from "@tanstack/vue-query"
import { toTypedSchema } from "@vee-validate/zod"
import { useUrlSearchParams } from "@vueuse/core"
import { useForm } from "vee-validate"
import { computed, ref, watchEffect } from "vue"
import { useRouter } from "vue-router"
import { z } from "zod"
import { getCustomTemplate, updateCustomTemplate } from "@/api"
import { Button, Card, Checkbox, Icon, Input, ReadMore, Switch } from "@/components/ui"
import { useTranslation } from "@/composables"
import { EventTemplate } from "@/models"

const props = withDefaults(defineProps<{ templateId?: number }>(), {
  templateId: undefined,
})

const emit = defineEmits<{
  (e: "save"): void
  (e: "success"): void
  (e: "error", err?: unknown): void
  (e: "back"): void
}>()

const router = useRouter()

const queryString = useUrlSearchParams<{ step?: number, hostAccountId?: number }>()
const { t } = useTranslation()

const isFormInitialized = ref(false)
const isNavigating = ref(false)

const validationSchema = toTypedSchema(
  z.object({
    tsAndCsLabel: z.string().optional(),
    tsAndCsLink: z.union([z.string().url(), z.literal("")]).optional(),
    optInLabel: z.string().optional(),
    optInLink: z.union([z.string().url(), z.literal("")]).optional(),
    requireAdditionalHostTerms: z.boolean().optional(),
    collectGuestEmailsForMarketing: z.boolean().optional(),
  }).superRefine((data, ctx) => {
    if (data.requireAdditionalHostTerms && !data.tsAndCsLabel) {
      ctx.addIssue({
        path: ["tsAndCsLabel"],
        message: t("template_legal_form.ts_and_cs_required"),
        code: z.ZodIssueCode.custom,
      })
    }
    if (data.collectGuestEmailsForMarketing && !data.optInLabel) {
      ctx.addIssue({
        path: ["optInLabel"],
        message: t("template_legal_form.opt_in_required"),
        code: z.ZodIssueCode.custom,
      })
    }
  }),
)

const { setFieldValue, values, handleSubmit } = useForm({
  validationSchema,
  initialValues: {
    tsAndCsLabel: undefined,
    tsAndCsLink: undefined,
    optInLabel: undefined,
    optInLink: undefined,
    requireAdditionalHostTerms: false,
    collectGuestEmailsForMarketing: false,
  },
})

const { data: customTemplate, isLoading: isLoadingTemplate } = useQuery({
  queryKey: ["getCustomTemplate", props.templateId],
  queryFn: () => getCustomTemplate(props.templateId),
  enabled: !!props.templateId,
})

const { mutate: updateTemplate, isPending: isUpdatingTemplate } = useMutation({
  mutationFn: (customTemplate: EventTemplate) => updateCustomTemplate(customTemplate),
  onSuccess: async () => {
    const redirect = queryString.hostAccountId
      ? `/template/${customTemplate.value?.id}/assign?hostAccountId=${queryString.hostAccountId}`
      : `/template/${customTemplate.value?.id}/assign`

    isNavigating.value = true
    router.push(redirect).finally(() => {
      isNavigating.value = false
    })
  },
  onError: (err) => {
    emit("error", err)
  },
})

const isLoading = computed(() => isLoadingTemplate.value || isUpdatingTemplate.value || isNavigating.value)

function initialiseFormValues(template: EventTemplate) {
  if (isFormInitialized.value)
    return

  setFieldValue("requireAdditionalHostTerms", template?.requireAdditionalHostTerms === true)
  setFieldValue("collectGuestEmailsForMarketing", template?.collectGuestEmailsForMarketing === true)
  setFieldValue("tsAndCsLabel", template?.additionalHostTerm?.key)
  setFieldValue("tsAndCsLink", template?.additionalHostTerm?.value ?? undefined)
  setFieldValue("optInLabel", template?.optInSentence?.key)
  setFieldValue("optInLink", template?.optInSentence?.value ?? undefined)

  isFormInitialized.value = true
}

const onSubmit = handleSubmit((values) => {
  emit("save")

  const template = new EventTemplate({
    ...customTemplate.value,
    requireAdditionalHostTerms: values.requireAdditionalHostTerms === true,
    collectGuestEmailsForMarketing: values.collectGuestEmailsForMarketing === true,
    additionalHostTerm: {
      key: values.tsAndCsLabel,
      value: values.tsAndCsLink,
    },
    optInSentence: {
      key: values.optInLabel,
      value: values.optInLink,
    },
  })

  updateTemplate(template)
})

watchEffect(() => {
  if (customTemplate.value)
    initialiseFormValues(customTemplate.value)
})
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 w-full mb-10">
      <div class="flex flex-col gap-2 mb-4">
        <h1 class="text-xl font-comfortaa font-bold">
          {{ t('template_legal_form.title') }}
        </h1>
        <div class="text-label">
          {{ t('template_legal_form.message') }}
        </div>
      </div>

      <div>
        <div v-if="isLoading">
          <div class="gpx-skeleton h-[200px] mb-8" />
          <div class="gpx-skeleton h-[200px]" />
        </div>

        <div v-show="!isLoading">
          <form>
            <div class="flex flex-col gap-8">
              <div class="flex flex-col gap-4">
                <h4 class="font-comfortaa font-bold text-lg">
                  {{ t('template_legal_form.ts_and_cs_header') }}
                </h4>
                <p>
                  {{ t('template_legal_form.ts_and_cs_details') }}
                </p>
                <Switch
                  :model-value="values.requireAdditionalHostTerms"
                  name="requireAdditionalHostTerms"
                  :label="t('template_legal_form.ts_and_cs_checkbox_label')"
                  @update:model-value="(v) => setFieldValue('requireAdditionalHostTerms', v)"
                />
                <div v-if="values.requireAdditionalHostTerms" class="grid md:grid-cols-2 gap-4">
                  <Input name="tsAndCsLabel" :label="t('template_legal_form.ts_and_cs_input_label')" />
                  <Input name="tsAndCsLink" :label="t('template_legal_form.ts_and_cs_input_link')" />
                </div>
              </div>

              <hr>

              <div class="flex flex-col gap-4">
                <h4 class="font-comfortaa font-bold text-lg">
                  {{ t('template_legal_form.marketing_header') }}
                </h4>
                <p>
                  {{ t('template_legal_form.marketing_details') }}
                </p>
                <Switch
                  :model-value="values.collectGuestEmailsForMarketing"
                  name="collectGuestEmailsForMarketing"
                  :label="t('template_legal_form.marketing_checkbox_label')"
                  @update:model-value="(v) => setFieldValue('collectGuestEmailsForMarketing', v)"
                />
                <div v-if="values.collectGuestEmailsForMarketing" class="grid md:grid-cols-2 gap-4">
                  <Input name="optInLabel" :label="t('template_legal_form.opt_in_input_label')" />
                  <Input name="optInLink" :label="t('template_legal_form.opt_in_input_link')" />
                </div>
              </div>
            </div>
          </form>

          <!-- Preview -->
          <div class="mt-14">
            <div class="flex gap-1 font-semibold font-comfortaa items-center mb-2">
              <Icon icon="EyeOn" />
              {{ t('template_legal_form.preview_header') }}
            </div>

            <Card variant="secondary" class="overflow-hidden max-w-[800px]" :with-padding="false">
              <div class="grid xl:grid-cols-2">
                <div class="p-8">
                  <h1 class="text-2xl" style="text-align: left; padding-bottom: 1.5rem">
                    My Event
                  </h1>
                  <h2 class="text-xl">
                    Message from the Host
                  </h2>
                  <br>
                  <ReadMore content="Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt. " />
                  <div>
                    <div class="flex flex-col gap-4 mt-8">
                      <Input name="name" label="Full name*" no-border />
                      <div v-if="values.requireAdditionalHostTerms" class="flex gap-[0.1rem]">
                        <Checkbox name="tsandcsPreview" label="" />
                        <a v-if="values.tsAndCsLink" class="underline" :href="values.tsAndCsLink">{{ values.tsAndCsLabel }}</a>
                        <p v-else>
                          {{ values.tsAndCsLabel }}
                        </p>
                      </div>
                      <div v-if="values.collectGuestEmailsForMarketing" class="flex gap-[0.1rem]">
                        <Checkbox name="marketingPreview" label="" />
                        <a v-if="values.optInLink" class="underline" :href="values.optInLink">{{ values.optInLabel }}</a>
                        <p v-else>
                          {{ values.optInLabel }}
                        </p>
                      </div>
                      <Button class="w-fit">
                        Continue
                      </Button>
                    </div>
                  </div>
                  <div class="mt-8 text-xs">
                    <span>
                      Use of this platform is subject to the <a href="#" class="underline">Guest Terms of Service</a> and
                      <a href="#" class="underline">Acceptable Use Policy</a>.
                    </span>
                  </div>
                </div>
                <div
                  class="bg-cover bg-center h-full w-auto object-cover filter grayscale hidden xl:flex items-center justify-center"
                  style="background-image: var(--gpx-default-cover-image)"
                />
              </div>
            </Card>
          </div>
        </div>
      </div>

      <div class="flex items-center mt-16 gap-2">
        <Button variant="secondary" prefix-icon="ArrowLeft" :disabled="isLoading" @click.prevent="() => emit('back')">
          {{ t('back') }}
        </Button>
        <Button class="w-fit px-10" :disabled="isLoading" @click.prevent="onSubmit">
          {{ isUpdatingTemplate ? t('saving') : t('save') }}
          <Icon v-if="isUpdatingTemplate" icon="Loading" />
        </Button>
      </div>
    </div>
  </div>
</template>
